import type { OpenAPIAwaitedResponse, OpenAPIResponse } from './api-switcher'
import { getTraceIdFromResponse } from '../utils/error-handling'

export type ApiError =
  | {
      type: 'http'
      status: number
      code?: number
      message?: string
      traceId?: string
    }
  | {
      type: 'network'
      message?: string
    }

export type ApiResult<T> =
  | {
      data: T
      error: undefined
      response: Response
    }
  | {
      data: undefined
      error: ApiError
      response?: Response
    }

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return undefined
}

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError'

const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError ||
  isAbortError(error)

const toHttpApiError = <T>({error, response}: Extract<OpenAPIAwaitedResponse<T>, {error: unknown}>): ApiError => ({
  type: 'http',
  status: response.status,
  code: typeof error.code === 'number' ? error.code : undefined,
  message: typeof error.message === 'string' ? error.message : undefined,
  traceId: getTraceIdFromResponse(response),
})

export const toApiResult = <T>(result: OpenAPIAwaitedResponse<T>): ApiResult<T> =>
  result.error ?
    {
      data: undefined,
      error: toHttpApiError(result),
      response: result.response,
    }
  :
    {
      data: result.data,
      error: undefined,
      response: result.response,
    }

type ToApiResultAsyncOptions = {
  throwAbort?: boolean
}

export const toApiResultAsync = async <T>(
  fetcher: () => OpenAPIResponse<T>,
  options?: ToApiResultAsyncOptions,
): Promise<ApiResult<T>> => {
  const response = fetcher()
  try {
    return toApiResult(await response)
  } catch (error) {
    if (options?.throwAbort && isAbortError(error)) throw error
    if (!isNetworkError(error)) throw error
    return {
      data: undefined,
      error: {
        type: 'network',
        message: getErrorMessage(error),
      },
    }
  }
}
